import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Modal, Form, Input, DatePicker, Select, Button } from 'antd';
import type { Moment } from 'moment';

import { AdCabinet, Source, TaskType } from './types';

// Название источника GetCourse
const GETCOURSE_SOURCE_NAME = 'GetCourse';
// Название типа задачи "Выгрузка рекламы"
const AD_EXPORT_TASK_TYPE_NAME = 'Выгрузка рекламы';

interface CreateTaskModalProps {
    open: boolean;
    onCancel: () => void;
    onCreate: (values: any) => Promise<boolean>;
    taskTypes: TaskType[];
    sources: Source[];
    adCabinets: AdCabinet[];
    schoolId?: number;
}

export const CreateTaskModal: React.FC<CreateTaskModalProps> = ({
    open,
    onCancel,
    onCreate,
    taskTypes,
    sources,
    adCabinets,
    schoolId
}) => {
    const [ form ] = Form.useForm();

    const [ selectedSourceId, setSelectedSourceId ] = useState<number | null>(null);

    // Определяем является ли выбранный источник GetCourse
    const isGetCourseSource = useMemo(() => {
        if (!selectedSourceId) return false;
        const source = sources.find(s => s.id === selectedSourceId);
        return source?.name?.toLowerCase() === 'getcourse';
    }, [ selectedSourceId, sources ]);

    // Фильтруем типы задач в зависимости от источника
    const filteredTaskTypes = useMemo(() => {
        if (!selectedSourceId) return [];

        if (isGetCourseSource) {
            // GetCourse: все типы кроме "Выгрузка рекламы"
            return taskTypes.filter(t => t.name !== AD_EXPORT_TASK_TYPE_NAME);
        } else {
            // Другие источники: только "Выгрузка рекламы"
            return taskTypes.filter(t => t.name === AD_EXPORT_TASK_TYPE_NAME);
        }
    }, [ selectedSourceId, isGetCourseSource, taskTypes ]);

    // Фильтруем рекламные кабинеты по выбранному источнику
    const filteredAdCabinets = useMemo(() => {
        if (!selectedSourceId || isGetCourseSource) return [];
        return adCabinets.filter(cabinet => cabinet.source_id === selectedSourceId);
    }, [ selectedSourceId, isGetCourseSource, adCabinets ]);

    // Доступность полей
    const isSourceSelected = selectedSourceId !== null;
    const isTaskTypeDisabled = !isSourceSelected;
    // Для GetCourse - поле ввода, для других источников - выбор рекламного кабинета
    const showAdCabinetSelect = !isGetCourseSource && isSourceSelected;

    // Сброс зависимых полей при смене источника и автовыбор типа задачи если он один
    const prevSourceIdRef = useRef<number | null>(null);
    const sourcesRef = useRef(sources);
    const taskTypesRef = useRef(taskTypes);
    sourcesRef.current = sources;
    taskTypesRef.current = taskTypes;

    useEffect(() => {
        if (selectedSourceId !== null && prevSourceIdRef.current !== selectedSourceId) {
            // Сбрасываем зависимые поля
            form.setFieldsValue({
                task_type_id: undefined,
                external_export_id: undefined
            });

            // Определяем GetCourse внутри эффекта
            const getCourseSource = sourcesRef.current.find(s => s.name === GETCOURSE_SOURCE_NAME);
            const isGetCourse = selectedSourceId === getCourseSource?.id;

            // Если не GetCourse — автоматически выбираем единственный тип задачи "Выгрузка рекламы"
            if (!isGetCourse) {
                const adExportType = taskTypesRef.current.find(t => t.name === AD_EXPORT_TASK_TYPE_NAME);
                if (adExportType) {
                    form.setFieldsValue({ task_type_id: adExportType.id });
                }
            }
        }
        prevSourceIdRef.current = selectedSourceId;
    }, [ selectedSourceId, form ]);

    // Валидация, зависящая от task_type_id и других полей
    const validateConditionalFields = (_: any, value: any) => {
        const taskTypeId = form.getFieldValue('task_type_id');
        const externalExportId = form.getFieldValue('external_export_id');
        const periodStartDate = form.getFieldValue('period_start_date');
        const periodEndDate = form.getFieldValue('period_end_date');

        // Для task_type_id = 1,2,3,4,5 обязателен external_export_id ИЛИ period_start_date/period_end_date
        if ([ 1, 2, 3, 4, 5 ].includes(taskTypeId)) {
            if (!externalExportId) {
                if (!periodStartDate || !periodEndDate) {
                    return Promise.reject(
                        new Error('Обязателен внешний ID экспорта или даты начала и окончания периода')
                    );
                }
            }
            if (periodStartDate && periodEndDate && periodEndDate.isBefore(periodStartDate)) {
                return Promise.reject(new Error('Дата окончания периода не может быть раньше даты начала'));
            }
        }
        
        return Promise.resolve();
    };

    const onFinish = async (values: any) => {
        const payload = {
            school_id: schoolId,
            source_id: values.source_id,
            task_type_id: values.task_type_id,
            external_export_id: values.external_export_id || null,
            period_start_date: values.period_start_date
                ? (values.period_start_date as Moment).format('YYYY-MM-DD')
                : null,
            period_end_date: values.period_end_date
                ? (values.period_end_date as Moment).format('YYYY-MM-DD')
                : null,
        };
        
        const success = await onCreate(payload);
        
        if (success) {
            form.resetFields();
            setSelectedSourceId(null);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setSelectedSourceId(null);
        onCancel();
    };

    const handleSourceChange = (value: number) => {
        setSelectedSourceId(value);
    };

    // Регистронезависимый поиск
    const filterOption = (input: string, option: any) => {
        const label = option?.label || option?.children || '';
        return String(label).toLowerCase().includes(input.toLowerCase());
    };

    return (
        <Modal
            open={open}
            title="Создать задачу"
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Отмена
                </Button>,
                <Button key="submit" type="primary" onClick={() => form.submit()}>
                    Создать
                </Button>,
            ]}
            destroyOnClose
        >
            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Form.Item
                    label="Источник данных"
                    name="source_id"
                    rules={[ { required: true, message: 'Выберите источник данных' } ]}
                >
                    <Select
                        placeholder="Выберите источник данных"
                        style={{ width: '100%' }}
                        showSearch
                        filterOption={filterOption}
                        allowClear
                        onChange={handleSourceChange}
                        options={sources.map(source => ({
                            label: source.name,
                            value: source.id
                        }))}
                    />
                </Form.Item>

                <Form.Item
                    label="Тип задачи"
                    name="task_type_id"
                    rules={[ { required: true, message: 'Выберите тип задачи' } ]}
                >
                    <Select
                        placeholder={isSourceSelected ? 'Выберите тип задачи' : 'Сначала выберите источник'}
                        style={{ width: '100%' }}
                        showSearch
                        filterOption={filterOption}
                        allowClear
                        disabled={isTaskTypeDisabled}
                        options={filteredTaskTypes.map(type => ({
                            label: type.name,
                            value: type.id
                        }))}
                    />
                </Form.Item>

                <Form.Item
                    label={showAdCabinetSelect ? 'Рекламный кабинет' : 'Внешний ID экспорта'}
                    name="external_export_id"
                    dependencies={['period_start_date', 'period_end_date', 'task_type_id']}
                    rules={[
                        {
                            required: showAdCabinetSelect,
                            message: 'Выберите рекламный кабинет'
                        },
                        {
                            validator: validateConditionalFields,
                        },
                    ]}
                >
                    {showAdCabinetSelect ? (
                        <Select
                            placeholder="Выберите рекламный кабинет"
                            style={{ width: '100%' }}
                            showSearch
                            filterOption={filterOption}
                            allowClear
                            options={filteredAdCabinets.map(cabinet => ({
                                label: `${cabinet.name} (#${cabinet.id})`,
                                value: cabinet.id
                            }))}
                        />
                    ) : (
                        <Input
                            placeholder={!isSourceSelected ? 'Сначала выберите источник' : 'Введите внешний ID экспорта'}
                            disabled={!isSourceSelected}
                            allowClear
                            onKeyPress={(e) => {
                                // Разрешаем только цифры
                                if (!/[0-9]/.test(e.key)) {
                                    e.preventDefault();
                                }
                            }}
                            onPaste={(e) => {
                                // При вставке оставляем только цифры
                                const paste = e.clipboardData.getData('text');
                                if (!/^\d+$/.test(paste)) {
                                    e.preventDefault();
                                }
                            }}
                        />
                    )}
                </Form.Item>

                <Form.Item
                    label="Дата начала периода"
                    name="period_start_date"
                    dependencies={['period_end_date', 'external_export_id', 'task_type_id']}
                    rules={[
                        {
                            validator: validateConditionalFields,
                        },
                    ]}
                >
                    <DatePicker
                        style={{ width: '100%' }}
                        placeholder="Выберите дату"
                        format="DD.MM.YYYY"
                    />
                </Form.Item>

                <Form.Item
                    label="Дата окончания периода"
                    name="period_end_date"
                    dependencies={['period_start_date', 'external_export_id', 'task_type_id']}
                    rules={[
                        {
                            validator: validateConditionalFields,
                        },
                    ]}
                >
                    <DatePicker
                        style={{ width: '100%' }}
                        placeholder="Выберите дату"
                        format="DD.MM.YYYY"
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};
